import { Box, Card, CardContent, Typography, Button, CardHeader } from '@mui/material';
import { Videocam, PlayArrow } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';

import { QUESTIONS, Question } from '@libs/types';
import { VideoViewOnlyModal } from '@carcareers/components';
import { useGetVideosQuery } from '@carcareers/store';

interface VideosViewCardProps {
  jobseekerId: number;
}

export const VideosViewCard: FC<VideosViewCardProps> = ({ jobseekerId }) => {
  const { data: videosData, isLoading } = useGetVideosQuery(jobseekerId, {
    skip: !jobseekerId,
  });

  const [questions, setQuestions] = useState<Question[]>(QUESTIONS);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

  const getVideoUrl = (videos: typeof videosData, questionId: number): string | null => {
    if (!videos) return null;
    switch (questionId) {
      case 1:
        return videos.question1Url;
      case 2:
        return videos.question2Url;
      case 3:
        return videos.question3Url;
      default:
        return null;
    }
  };

  const handleViewVideo = (questionId: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      setSelectedQuestion(question);
      setViewModalOpen(true);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedQuestion(null);
  };

  // Update questions based on API data
  useEffect(() => {
    if (videosData) {
      setQuestions((prev) =>
        prev.map((q) => ({
          ...q,
          isAnswered: getVideoUrl(videosData, q.id) !== null,
          videoUrl: getVideoUrl(videosData, q.id) || undefined,
        })),
      );
    }
  }, [videosData]);

  return (
    <Card elevation={0} sx={{ height: '100%', borderRadius: 3 }}>
      <CardHeader
        title="Video Answers"
        avatar={<Videocam sx={{ color: 'primary.contrastText' }} />}
        sx={{
          backgroundColor: 'primary.main',
          '& .MuiCardHeader-title': { color: 'primary.contrastText' },
        }}
      />
      <CardContent sx={{ p: '16px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {questions.map((question) => (
            <Box
              key={question.id}
              sx={{
                py: 2,
                px: 2,
                display: 'flex',
                borderRadius: 3,
                justifyContent: 'space-between',
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {question.text}
                </Typography>
              </Box>

              {question.isAnswered ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrow />}
                  onClick={() => handleViewVideo(question.id)}
                  sx={{ minWidth: 'fit-content' }}
                  disabled={isLoading}
                >
                  View
                </Button>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    fontStyle: 'italic',
                    alignSelf: 'center',
                  }}
                >
                  Not answered
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Video answers provided by the jobseeker.
        </Typography>
      </CardContent>

      <VideoViewOnlyModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        videoUrl={selectedQuestion?.videoUrl || null}
        questionText={selectedQuestion?.text}
      />
    </Card>
  );
};
